import React, { useState, useEffect } from 'react';
import type { ErrorInfo } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import ErrorBoundary from '../../components/ErrorBoundary';
import { useCourse } from '../../contexts/CourseContext';
import { useConsentStatus } from '../../hooks/useConsentStatus';
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Container,
  FormControl,
  VStack,
  Text,
  Heading,
  useToast,
  Textarea,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
} from '@chakra-ui/react';

// Custom error fallback for preferences page
const PreferencesErrorFallback = ({ error, resetErrorBoundary }: { error: Error | null, resetErrorBoundary: () => void }) => {
  const navigate = useNavigate();
  const [isReloading, setIsReloading] = useState(false);

  const handleReset = () => {
    setIsReloading(true);
    resetErrorBoundary();
    window.location.reload();
  };

  return (
    <Container maxW="container.lg" py={8}>
      <Alert status="error" borderRadius="md" mb={6}>
        <AlertIcon />
        <Box flex="1">
          <AlertTitle>Preferences Error</AlertTitle>
          <AlertDescription>
            We're having trouble loading your preferences. Please try refreshing the page.
          </AlertDescription>
          {process.env.NODE_ENV === 'development' && error && (
            <Box mt={2} p={2} bg="blackAlpha.100" borderRadius="md" fontSize="sm">
              <Text fontWeight="bold">Error details:</Text>
              <Text as="pre" whiteSpace="pre-wrap" fontFamily="mono">
                {error.message}
              </Text>
            </Box>
          )}
        </Box>
      </Alert>
      
      <VStack spacing={4}>
        <Button
          colorScheme="blue"
          onClick={handleReset}
          isLoading={isReloading}
          loadingText="Reloading..."
          leftIcon={<Box as="span" className="material-icons">refresh</Box>}
        >
          Reload Preferences
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate('/')}
          isDisabled={isReloading}
        >
          Return to Course
        </Button>
      </VStack>
    </Container>
  );
};

// Define types for preferences

interface PreferenceCategory {
  title: string;
  options: string[];
}

// Main PreferencesPage component
const PreferencesPageContent = () => {
  const { currentCourse } = useCourse();
  const { loading: loadingConsent, consentGiven } = useConsentStatus();
  const toast = useToast();
  const navigate = useNavigate();
  
  // State for form fields
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedPreferences, setSelectedPreferences] = useState<string[]>([]);
  const [preferenceString, setPreferenceString] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const customPrefRef = React.useRef<HTMLTextAreaElement>(null);

  // Load saved preferences on component mount
  useEffect(() => {
    if (!currentCourse) {
      setIsLoading(false);
      return;
    }

    const fetchPreferences = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { data, error } = await supabase
          .from('course_learner_preferences')
          .select('learning_preference, course_preferred_name')
          .eq('user_id', user.id)
          .eq('course_id', currentCourse.id)
          .single();

        if (error && error.code !== 'PGRST116') { // Ignore "no rows" error
          console.error('Error fetching preferences:', error);
          return;
        }

        if (data?.learning_preference) {
          const savedText = data.learning_preference;
          const allOptions = preferenceCategories.flatMap(cat => cat.options);
          const matched: string[] = [];

          allOptions.forEach(opt => {
            if (savedText.includes(opt)) {
              matched.push(opt);
            }
          });
          setSelectedPreferences(matched);
          setPreferenceString(savedText);
        }

        if (data?.course_preferred_name) {
          setPreferredName(data.course_preferred_name);
        }

      } catch (error) {
        console.error('Error loading preferences:', error);
        toast({
          title: 'Error',
          description: 'Failed to load preferences',
          status: 'error',
          duration: 5000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreferences();
  }, [currentCourse, toast]);

  const handleCheckboxChange = (values: (string | number)[]) => {
    setSelectedPreferences(values.map(String));
    setPreferenceString(values.join(', '));
  };

  const handleCustomPrefInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const currentValue = e.target.value;
    const allOptions = preferenceCategories.flatMap(cat => cat.options);
    const matched: string[] = [];
    let textToParse = currentValue;

    allOptions.forEach(opt => {
      if (textToParse.includes(opt)) {
        matched.push(opt);
        textToParse = textToParse.replace(opt, '');
      }
    });
    textToParse = textToParse.replace(/^,|,$/g, '').replace(/, ,/g, ',').trim();

    setSelectedPreferences(matched);
    setPreferenceString(currentValue);
  };

  const minLength = 10;
  const handleSubmit = async () => {
    if (!currentCourse) return;
    
    setIsSaving(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      if (preferenceString.length < minLength) {
        throw new Error(`Please provide at least ${minLength} characters describing your learning preferences.`);
      }
      
      const { error } = await supabase
        .from('course_learner_preferences')
        .upsert(
          {
            user_id: user.id,
            course_id: currentCourse.id,
            learning_preference: preferenceString || null,
            course_preferred_name: preferredName.trim() || null,
            updated_at: new Date().toISOString()
          },
          { onConflict: 'user_id,course_id' }
        )
        .select();
      
      if (error) throw error;
      
      toast({
        title: 'Success',
        description: 'Preferences saved successfully',
        status: 'success',
        duration: 3000,
      });
      // Continue to activity page
      navigate('/s/activity');
      
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save preferences. Please try again.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };
  // Loading state
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="50vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  
  if (!currentCourse) {
    return (
      <Box textAlign="center" py={10} px={6}>
        <Heading as="h2" size="xl" mt={6} mb={2}>
          No Course Assigned
        </Heading>
        <Text color={'gray.500'} mb={6}>
          You haven't been assigned to a course yet. Please contact your administrator.
        </Text>
        <Button
          colorScheme="purple"
          onClick={() => window.location.href = '/'}
        >
          Return to Home
        </Button>
      </Box>
    );
  }

  // Gate access: if consent not given yet for this course, redirect to landing
  if (loadingConsent) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="50vh">
        <Spinner size="xl" />
      </Box>
    );
  }
  if (!consentGiven) {
    return <Navigate to="/s/landing" replace />;
  }

  return (
    <Box bg="white" minH="100vh" py={12}>
      <Container maxW="4xl">
        <VStack spacing={6} textAlign="left" mb={8}>
          <Box>
            <Heading as="h1" size="2xl" mb={3} color="gray.800">Learning Preferences</Heading>
            <Text color="gray.600">The AI behind the Tutor can use your preferences to help you learn</Text>
          </Box>
        </VStack>

        {/* Preferences Section */}
        <Box 
          bg="white" 
          p={8} 
          borderRadius="xl" 
          mb={8}
          borderWidth="2px"
          borderColor="#C200FB"
          boxShadow="sm"
        >
          <Heading as="h2" size="lg" color="#C200FB" mb={4}>
            Select your preferences
          </Heading>
          <VStack spacing={6} align="stretch">
            <FormControl>
              <CheckboxGroup 
                colorScheme="blue" 
                value={selectedPreferences}
                onChange={handleCheckboxChange}
              >
                <Box 
                  display="flex" 
                  flexDirection={{ base: 'column', md: 'row' }}
                  gap={{ base: 6, md: 4 }}
                  width="100%"
                  alignItems="stretch"
                >
                  {preferenceCategories.map((category, index) => {
                    // Define border colors for each card
                    const borderColors = ['#17FFC4', '#FF8811', '#1AC8ED'];
                    return (
                    <Box 
                      key={category.title}
                      flex={{ base: '0 0 auto', md: '1' }}
                      width={{ base: '100%', md: 'auto' }}
                      bg="white"
                      p={4}
                      borderRadius="md"
                      border="1px solid"
                      borderColor={borderColors[index % borderColors.length]}
                      display="flex"
                      flexDirection="column"
                    >
                      <Text fontWeight="bold" mb={3} color="gray.700">
                        {category.title}
                      </Text>
                      <VStack spacing={3} align="stretch" width="100%" flex="1">
                        {category.options.map((pref) => (
                          <Box 
                            key={pref}
                            display="flex"
                            alignItems="flex-start"
                            gap={2}
                          >
                            <Checkbox 
                              value={pref}
                              size="md"
                              colorScheme="blue"
                              mt={1} // Slight top margin to align with first line of text
                            />
                            <Text fontSize="sm" textAlign="left">
                              {pref}
                            </Text>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  );
                  })}
                </Box>
              </CheckboxGroup>
              
              <Text mt={6} mb={4} color="gray.800" fontSize="lg" textAlign="center">
                Don't see the preferences that you need? You can type directly into the field below.
              </Text>
            </FormControl>
          </VStack>
        </Box>

        {/* Custom Preferences Section */}
        <Box 
          bg="white" 
          p={8} 
          borderRadius="xl" 
          mb={8}
          borderWidth="2px"
          borderColor="#1AC8ED"
          boxShadow="sm"
        >
          <VStack spacing={6} align="stretch">
            <FormControl>
              <Heading as="h2" size="lg" color="#C200FB" mb={4}>
                Your Custom Learning Preferences
              </Heading>
              <Text mb={3} color="gray.600" fontSize="md">
                Selected preferences will appear here. Edit or add your own preferences as needed.
              </Text>
              <Textarea
                ref={customPrefRef}
                value={preferenceString}
                onChange={handleCustomPrefInputChange}
                placeholder="Type your specific learning preferences here..."
                mt={2}
                minH="100px"
                resize="vertical"
                autoCapitalize="sentences"
                autoCorrect="on"
                spellCheck="true"
                autoComplete="off"
              />
            </FormControl>

            <Button
              colorScheme="blue"
              size="lg"
              width="full"
              onClick={handleSubmit}
              isLoading={isSaving}
              isDisabled={isSaving || preferenceString.length < minLength}
              loadingText="Saving & Continuing..."
              mt={4}
            >
              Save & Continue
            </Button>
            {preferenceString.length < minLength && (
              <Text color="red.500" fontSize="sm" mt={2} textAlign="center">
                Please enter at least {minLength} characters to continue.
              </Text>
            )}
          </VStack>
        </Box>
      </Container>
    </Box>
  );
}

// Define the structure for preference options in three categories
const preferenceCategories: PreferenceCategory[] = [
  {
    title: 'Clear',
    options: [
      'Avoid long words or complex phrases',
      'Keep sentences under 15 words',
      'Avoid using sayings or figures of speech',
    ]
  },
  {
    title: 'Structured',
    options: [
      'Use dot points instead of paragraphs',
      'Add headings for each section',
      'Number steps in a process',
    ]
  },
  {
    title: 'Detailed',
    options: [
      'Include real-world examples',
      'Provide deeper explanations',
      'Use analogies to explain ideas',
    ]
  }
];

// Export the wrapped component
export function PreferencesPage() {
  return (
    <ErrorBoundary 
      fallback={<PreferencesErrorFallback error={null} resetErrorBoundary={() => {}} />}
      onError={(error: Error, errorInfo: ErrorInfo) => {
        console.error('PreferencesPage error boundary caught an error:', error, errorInfo);
        // You could also log this to an error tracking service
      }}
    >
      <PreferencesPageContent />
    </ErrorBoundary>
  );
}
